import { css, html, LitElement } from 'lit';
import { defineDemoElement, demoLayoutStyles, stripIndent } from './demo-shared.js';
import { state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';
import type { WebModuleTable, WebModuleTableColumnDef, WebModuleTableItemMove } from '../../components/table2/table.js';

interface DemoTableRow {
  id: number;
  name: string;
  category: string;
  status: 'Active' | 'Draft' | 'Archived';
  qty: number;
  price: number;
  /** Long free-text value used by the cell-fade demo. */
  spec?: string;
  /** When true the row is pinned (non-draggable) - kept at the end of the list. */
  locked?: boolean;
}

function money(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function statusVariant(status: DemoTableRow['status']): string {
  return status === 'Active' ? 'success' : status === 'Draft' ? 'warning' : 'neutral';
}

const tableStageStyles = css`
  :host {
    display: block;
  }
`;

/** Grouping, sticky header, pinned columns, aggregates and group-header drops. */
class DemoTableGrouped extends LitElement {
  static styles = [demoLayoutStyles, tableStageStyles];

  // Sorted by category so grouped rows are adjacent.
  @state() private rows: DemoTableRow[] = [
    { id: 1, name: 'Awning Window 600x900', category: 'Windows', status: 'Active', qty: 12, price: 245.5 },
    { id: 2, name: 'Sliding Window 1200x1200', category: 'Windows', status: 'Active', qty: 8, price: 410.0 },
    { id: 3, name: 'Fixed Lite 900x900', category: 'Windows', status: 'Draft', qty: 5, price: 180.75 },
    { id: 4, name: 'Hinged Door 820x2040', category: 'Doors', status: 'Active', qty: 6, price: 620.0 },
    { id: 5, name: 'Sliding Door 1800x2100', category: 'Doors', status: 'Archived', qty: 3, price: 980.25 },
    { id: 6, name: 'French Door Pair', category: 'Doors', status: 'Active', qty: 2, price: 1340.0 },
    { id: 7, name: 'Flyscreen Roll 30m', category: 'Hardware', status: 'Active', qty: 20, price: 75.0 },
    { id: 8, name: 'Lock Set - Euro', category: 'Hardware', status: 'Draft', qty: 35, price: 42.9 }
  ];

  private get columns(): WebModuleTableColumnDef[] {
    return [
      { title: 'Product', fieldName: 'name', fixed: 'left', width: '220px', sortable: true },
      {
        title: 'Status',
        fieldName: 'status',
        align: 'center',
        width: '120px',
        displayValue: (_t, item: DemoTableRow) =>
          html`<webmodule-tag variant=${statusVariant(item.status)}>${item.status}</webmodule-tag>`
      },
      {
        title: 'Qty',
        fieldName: 'qty',
        align: 'right',
        width: '90px',
        sortable: true,
        groupAggregate: (rows: DemoTableRow[]) => String(rows.reduce((sum, row) => sum + row.qty, 0)),
        columnAggregate: () => String(this.rows.reduce((sum, row) => sum + row.qty, 0))
      },
      {
        title: 'Line Total',
        fieldName: 'price',
        align: 'right',
        width: '140px',
        displayValue: (_t, item: DemoTableRow) => money(item.price * item.qty),
        groupAggregate: (rows: DemoTableRow[]) => money(rows.reduce((sum, row) => sum + row.price * row.qty, 0)),
        columnAggregate: () => money(this.rows.reduce((sum, row) => sum + row.price * row.qty, 0))
      },
      {
        title: '',
        fieldName: '_actions',
        fixed: 'right',
        width: '64px',
        align: 'center',
        displayValue: () => html`<webmodule-icon-button name="chevron-right" label="View"></webmodule-icon-button>`
      }
    ];
  }

  render() {
    return html`
      <div style="max-width: 560px;">
        <webmodule-table
          .columns=${this.columns}
          .data=${this.rows}
          .keyevent=${(item: DemoTableRow) => item.id}
          .groupBy=${'category'}
          .collapsibleGroups=${true}
          .bodyMaxHeight=${'280px'}
          .enableDrag=${true}
          .groupDropPosition=${'end'}
          @webmodule-table-item-move=${(e: CustomEvent) => this.handleMove(e)}
        ></webmodule-table>
      </div>
    `;
  }

  // A drop onto a group header carries the target group, so the moved row is
  // re-homed into that group (and kept contiguous with it).
  private handleMove(e: CustomEvent) {
    const { sourceIndex, targetIndex, targetGroupValue } = e.detail as WebModuleTableItemMove;
    const rows = [...this.rows];
    const moved = rows[sourceIndex];
    const destCategory =
      targetGroupValue !== undefined ? String(targetGroupValue) : (rows[targetIndex]?.category ?? moved.category);
    rows.splice(sourceIndex, 1);
    // targetIndex was computed against the pre-removal array.
    const insertAt = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    moved.category = destCategory;
    rows.splice(insertAt, 0, moved);
    this.rows = rows;
  }
}

/** Drag & drop reordering, locked rows and expandable rows. */
class DemoTableReorder extends LitElement {
  static styles = [demoLayoutStyles, tableStageStyles];

  @state() private rows: DemoTableRow[] = [
    { id: 11, name: 'Step 1 - Measure', category: '', status: 'Active', qty: 1, price: 0 },
    { id: 12, name: 'Step 2 - Quote', category: '', status: 'Active', qty: 1, price: 0 },
    { id: 13, name: 'Step 3 - Manufacture', category: '', status: 'Draft', qty: 1, price: 0 },
    { id: 14, name: 'Step 4 - Install', category: '', status: 'Draft', qty: 1, price: 0 },
    // Locked rows carry no grab handle and stay pinned at the end of the list.
    { id: 15, name: 'Step 5 - Sign-off (locked)', category: '', status: 'Active', qty: 1, price: 0, locked: true }
  ];

  @state() private allExpanded = false;

  // Shared expander provider: receives the row item, so the same provider works
  // for a single row (toggleExtension) and for all rows (expandAll/toggleAll).
  private rowExtension = (item: unknown) => {
    const row = item as DemoTableRow;
    return {
      template: html`<div style="display: flex; gap: 2rem; flex-wrap: wrap;">
        <div><strong>${row.name}</strong></div>
        <div>Reference: <code>#${row.id}</code></div>
        <div>Current status: ${row.status}</div>
        <div>Free-form expander content - any HTML can live here.</div>
      </div>`
    };
  };

  private get columns(): WebModuleTableColumnDef[] {
    return [
      {
        title: '',
        fieldName: '_expand',
        fixed: 'left',
        width: '2.5rem',
        align: 'center',
        displayValue: (table, item: DemoTableRow) =>
          html`<webmodule-icon-button
            name=${table.isExpanded(item) ? 'chevron-down' : 'chevron-right'}
            label="Expand"
            @click=${(ev: Event) => {
              ev.stopPropagation();
              table.toggleExtension(item, this.rowExtension);
            }}
          ></webmodule-icon-button>`
      },
      {
        id: 'task',
        label: 'Task',
        fieldName: 'name',
        minWidth: '220px',
        // Header action that expands/collapses every row's expander at once.
        headerActions: html`<webmodule-icon-button
          name=${this.allExpanded ? 'chevron-down' : 'chevron-right'}
          label=${this.allExpanded ? 'Collapse all' : 'Expand all'}
          @click=${(e: Event) => this.toggleExpandAll(e)}
        ></webmodule-icon-button>`
      },
      {
        title: 'Status',
        fieldName: 'status',
        align: 'center',
        width: '120px',
        displayValue: (_t, item: DemoTableRow) =>
          html`<webmodule-tag variant=${statusVariant(item.status)}>${item.status}</webmodule-tag>`
      }
    ];
  }

  render() {
    return html`
      <webmodule-table
        .columns=${this.columns}
        .data=${this.rows}
        .keyevent=${(item: DemoTableRow) => item.id}
        .enableDrag=${true}
        .checkEnabledDragEvent=${(item: DemoTableRow) => !item.locked}
        @webmodule-table-item-move=${(e: CustomEvent) => this.handleMove(e)}
      ></webmodule-table>
    `;
  }

  private toggleExpandAll(e: Event) {
    const table = ((e.currentTarget as HTMLElement).getRootNode() as ShadowRoot).host as WebModuleTable | undefined;

    if (!table) {
      return;
    }

    table.toggleAll(this.rowExtension);
    this.allExpanded = table.areAllExpanded();
  }

  private handleMove(e: CustomEvent) {
    const { sourceIndex, targetIndex } = e.detail as WebModuleTableItemMove;
    const rows = [...this.rows];
    const [moved] = rows.splice(sourceIndex, 1);
    rows.splice(targetIndex, 0, moved);
    this.rows = rows;
  }
}

/** Server-style paging via the fetch event, plus header cell slots. */
class DemoTablePaged extends LitElement {
  static styles = [demoLayoutStyles, tableStageStyles];

  // Full dataset backing the paged example (server-style paging via `fetch`).
  private readonly allRows: DemoTableRow[] = Array.from({ length: 43 }, (_, i) => ({
    id: 100 + i,
    name: `Order #${1000 + i}`,
    category: '',
    status: i % 3 === 0 ? 'Active' : i % 3 === 1 ? 'Draft' : 'Archived',
    qty: ((i * 7) % 9) + 1,
    price: Math.round((50 + ((i * 37) % 900)) * 100) / 100
  }));

  @state() private pageRows: DemoTableRow[] = this.allRows.slice(0, 10);

  private get columns(): WebModuleTableColumnDef[] {
    return [
      { id: 'order', label: 'Order', fieldName: 'name', minWidth: '220px', sortable: true },
      {
        title: 'Status',
        fieldName: 'status',
        align: 'center',
        width: '120px',
        displayValue: (_t, item: DemoTableRow) =>
          html`<webmodule-tag variant=${statusVariant(item.status)}>${item.status}</webmodule-tag>`
      },
      { title: 'Qty', fieldName: 'qty', align: 'right', width: '90px' },
      {
        title: 'Total',
        fieldName: 'price',
        align: 'right',
        width: '130px',
        displayValue: (_t, item: DemoTableRow) => money(item.price)
      }
    ];
  }

  render() {
    return html`
      <webmodule-table
        .columns=${this.columns}
        .data=${this.pageRows}
        .keyevent=${(item: DemoTableRow) => item.id}
        .rowCount=${this.allRows.length}
        .pageLength=${10}
        .showPaginationInfo=${true}
        .pageSizeOptions=${[10, 25, 50]}
        @fetch=${(e: CustomEvent) => this.loadPage(e)}
      >
        <webmodule-icon slot="order-prefix" name="chevron-right"></webmodule-icon>
        <webmodule-badge slot="order-suffix" variant="neutral" pill>ID</webmodule-badge>
        <webmodule-icon-button
          slot="order-header-actions"
          name="chevron-down"
          label="Column menu"
        ></webmodule-icon-button>
      </webmodule-table>
    `;
  }

  private loadPage(e: CustomEvent) {
    const pageIndex = (e.detail?.pageIndex as number) ?? 0;
    const pageLength = (e.detail?.pageLength as number) ?? 10;
    const start = pageIndex * pageLength;
    this.pageRows = this.allRows.slice(start, start + pageLength);
  }
}

/** Live playground for the appearance flags and semantic row tinting. */
class DemoTableAppearance extends LitElement {
  static styles = [demoLayoutStyles, tableStageStyles];

  private readonly rows: DemoTableRow[] = [
    { id: 21, name: 'Order #2001', category: '', status: 'Active', qty: 3, price: 420.0 },
    { id: 22, name: 'Order #2002 (overdue)', category: '', status: 'Draft', qty: 1, price: 980.5 },
    { id: 23, name: 'Order #2003', category: '', status: 'Active', qty: 7, price: 132.9 },
    { id: 24, name: 'Order #2004 (overdue)', category: '', status: 'Archived', qty: 2, price: 610.0 }
  ];

  @state() private elevated = true;
  @state() private cellBorders = true;
  @state() private uppercaseHeader = true;
  @state() private tintRows = true;

  private get columns(): WebModuleTableColumnDef[] {
    return [
      { title: 'Order', fieldName: 'name', minWidth: '200px' },
      {
        title: 'Status',
        fieldName: 'status',
        align: 'center',
        width: '120px',
        displayValue: (_t, item: DemoTableRow) =>
          html`<webmodule-tag variant=${statusVariant(item.status)}>${item.status}</webmodule-tag>`
      },
      {
        title: 'Total',
        fieldName: 'price',
        align: 'right',
        width: '120px',
        displayValue: (_t, item: DemoTableRow) => money(item.price)
      }
    ];
  }

  render() {
    return html`
      <div class="demo-row" style="margin-bottom: var(--webmodule-spacing-large);">
        <webmodule-toggle size="small" ?checked=${this.elevated} @webmodule-change=${this.flag('elevated')}>
          elevated
        </webmodule-toggle>
        <webmodule-toggle size="small" ?checked=${this.cellBorders} @webmodule-change=${this.flag('cellBorders')}>
          cellBorders
        </webmodule-toggle>
        <webmodule-toggle
          size="small"
          ?checked=${this.uppercaseHeader}
          @webmodule-change=${this.flag('uppercaseHeader')}
        >
          uppercaseHeader
        </webmodule-toggle>
        <webmodule-toggle size="small" ?checked=${this.tintRows} @webmodule-change=${this.flag('tintRows')}>
          rowVariantEvent
        </webmodule-toggle>
      </div>

      <webmodule-table
        .columns=${this.columns}
        .data=${this.rows}
        .keyevent=${(item: DemoTableRow) => item.id}
        .elevated=${this.elevated}
        .cellBorders=${this.cellBorders}
        .uppercaseHeader=${this.uppercaseHeader}
        .rowVariantEvent=${(item: DemoTableRow) =>
          this.tintRows && item.name.includes('overdue') ? 'danger' : undefined}
      ></webmodule-table>
    `;
  }

  private flag(name: 'elevated' | 'cellBorders' | 'uppercaseHeader' | 'tintRows') {
    return (e: Event) => {
      this[name] = (e.target as HTMLInputElement & { checked: boolean }).checked;
    };
  }
}

/** Per-column cell fading for long string values, with the master off switch. */
class DemoTableCellFade extends LitElement {
  static styles = [demoLayoutStyles, tableStageStyles];

  @state() private disableFade = false;

  private readonly rows: DemoTableRow[] = [
    {
      id: 31,
      name: 'Sliding Door',
      category: '',
      status: 'Active',
      qty: 1,
      price: 0,
      spec: 'SoftTech / Windows / Sliding Door / 2400 x 2100 / Anthracite Grey RAL 7016 / Double Glazed / Handle Left'
    },
    {
      id: 32,
      name: 'Awning Window',
      category: '',
      status: 'Draft',
      qty: 1,
      price: 0,
      spec: 'Awning Window / 1200 x 900 / White RAL 9016 / Obscure Glazed / Restrictor Stays / Trickle Vents Fitted'
    },
    {
      id: 33,
      name: 'French Doors',
      category: '',
      status: 'Active',
      qty: 1,
      price: 0,
      spec: 'French Door Pair / 1500 x 2100 / Chartwell Green / Georgian Bars / Multipoint Lock / Gold Furniture'
    }
  ];

  private get columns(): WebModuleTableColumnDef[] {
    return [
      { title: 'Product', fieldName: 'name', width: '150px', sortable: true },
      {
        // Opt just this column in. `true` uses the cell defaults: horizontal,
        // scrollbar hidden, wheel left to the page (drag or keyboard to scroll).
        title: 'Specification',
        fieldName: 'spec',
        minWidth: '240px',
        fade: true
      },
      {
        title: 'Status',
        fieldName: 'status',
        align: 'center',
        width: '120px',
        displayValue: (_t, item: DemoTableRow) =>
          html`<webmodule-tag variant=${statusVariant(item.status)}>${item.status}</webmodule-tag>`
      }
    ];
  }

  render() {
    return html`
      <div class="demo-row" style="margin-bottom: var(--webmodule-spacing-large);">
        <webmodule-toggle
          size="small"
          ?checked=${this.disableFade}
          @webmodule-change=${(e: Event) => (this.disableFade = (e.target as HTMLInputElement).checked)}
        >
          disableCellFade
        </webmodule-toggle>
      </div>
      <div style="max-width: 560px;">
        <webmodule-table
          .columns=${this.columns}
          .data=${this.rows}
          .keyevent=${(item: DemoTableRow) => item.id}
          .disableCellFade=${this.disableFade}
        ></webmodule-table>
      </div>
    `;
  }
}

export function registerTableDemos() {
  defineDemoElement('demo-table-grouped', DemoTableGrouped);
  defineDemoElement('demo-table-reorder', DemoTableReorder);
  defineDemoElement('demo-table-paged', DemoTablePaged);
  defineDemoElement('demo-table-appearance', DemoTableAppearance);
  defineDemoElement('demo-table-cell-fade', DemoTableCellFade);
}

export const tableSection: DemoSection = {
  id: 'table',
  title: 'Table',
  tags: ['webmodule-table'],
  summary:
    'The successor to webmodule-lit-table. It adds grouping with collapsible headers and footers, sticky headers, ' +
    'pinned columns, drag & drop reordering, expandable rows and appearance controls; migrate screens one at a time - ' +
    'the original table is untouched.',
  render: () => html`
    <demo-example
      label="Grouping, sticky header &amp; pinned columns"
      .code=${stripIndent(`
        html\`<webmodule-table
          .columns=\${columns}
          .data=\${rowsSortedByCategory}
          .groupBy=\${'category'}
          .collapsibleGroups=\${true}
          .bodyMaxHeight=\${'280px'}
          .enableDrag=\${true}
          .groupDropPosition=\${'end'}
          @webmodule-table-item-move=\${onMove}
        ></webmodule-table>\`;

        // Columns define aggregates for group footers and the grand total:
        { title: 'Qty', fieldName: 'qty', groupAggregate: rows => sum(rows), columnAggregate: () => total() }`)}
    >
      <div slot="description">
        Grouped by <code>category</code> (click a group header to collapse it), with per-group footers and a grand-total
        footer. <strong>Product</strong> is pinned left, <strong>Actions</strong> pinned right - scroll sideways to see
        them stick. Drag a row onto a <strong>group header</strong> to move it into that group.
      </div>
      <demo-table-grouped></demo-table-grouped>
    </demo-example>

    <demo-example
      label="Drag &amp; drop reordering + expandable rows"
      .code=${stripIndent(`
        html\`<webmodule-table
          .enableDrag=\${true}
          .checkEnabledDragEvent=\${item => !item.locked}
          @webmodule-table-item-move=\${e => reorder(e.detail.sourceIndex, e.detail.targetIndex)}
        ></webmodule-table>\`;

        // Expanders (single row and bulk):
        table.toggleExtension(item, provider);
        table.toggleAll(provider);`)}
    >
      <div slot="description">
        Drag the handle to reorder rows. The last row is <strong>locked</strong> (<code>checkEnabledDragEvent</code>
        returns false): no grab handle, cannot be dragged, rejects drops. Click a chevron to expand a row; the chevron
        in the <strong>Task</strong> header is a header action that expands or collapses every row at once.
      </div>
      <demo-table-reorder></demo-table-reorder>
    </demo-example>

    <demo-example
      label="Server-style paging"
      .code=${stripIndent(`
        html\`<webmodule-table
          .columns=\${columns}
          .data=\${pageRows}
          .rowCount=\${totalCount}
          .pageLength=\${10}
          .showPaginationInfo=\${true}
          .pageSizeOptions=\${[10, 25, 50]}
          @fetch=\${e => loadPage(e.detail.pageIndex, e.detail.pageLength)}
        ></webmodule-table>\`;`)}
    >
      <div slot="description">
        With <code>pageLength</code> and <code>rowCount</code> set, the paginator renders and a <code>fetch</code> event
        fires per page; the host loads that slice into <code>.data</code>. The <strong>Order</strong> header also shows
        the per-column header slots (<code>order-prefix</code>, <code>order-suffix</code>,
        <code>order-header-actions</code>).
      </div>
      <demo-table-paged></demo-table-paged>
    </demo-example>

    <demo-example
      label="Appearance &amp; row tinting"
      .code=${stripIndent(`
        html\`<webmodule-table
          .elevated=\${false}
          .cellBorders=\${false}
          .uppercaseHeader=\${false}
          .rowVariantEvent=\${item => (item.overdue ? 'danger' : undefined)}
        ></webmodule-table>\`;`)}
    >
      <div slot="description">
        <code>elevated</code> toggles the raised card frame, <code>cellBorders</code> the vertical body borders and
        <code>uppercaseHeader</code> the header casing. <code>rowVariantEvent</code> tints rows semantically
        (danger/warning/success/info/primary) with built-in hover states.
      </div>
      <demo-table-appearance></demo-table-appearance>
    </demo-example>

    <demo-example
      label="Cell fading for long values"
      .code=${stripIndent(`
        // Opt a column in with 'fade'. 'true' uses the cell defaults (horizontal,
        // scrollbar hidden, wheel left to the page - drag or keyboard to scroll):
        { title: 'Specification', fieldName: 'spec', minWidth: '240px', fade: true }
        // ...or tune it: fade: { orientation: 'vertical', withoutScrollbar: false }

        // Turn it off across the whole table in one place:
        html\`<webmodule-table .disableCellFade=\${true}></webmodule-table>\`;
        // (DataTableWrapper subclasses override disableCellFade() instead.)`)}
    >
      <div slot="description">
        Only the <strong>Specification</strong> column sets <code>fade: true</code>, so its long value fades and scrolls
        (drag to pan) instead of wrapping; the other columns are untouched. Toggle <code>disableCellFade</code> to drop
        the whole table back to plain wrapping cells.
      </div>
      <demo-table-cell-fade></demo-table-cell-fade>
    </demo-example>
  `
};
